import {
  getRequest,
  patchRequest,
  postRequest,
} from "common/HttpClient/http-utility";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";

const buildCustomerUsersUrl = (root, customerId) =>
  `${root}/${encodeURIComponent(String(customerId))}/users`;

const buildCustomerUserInvitesUrl = (root, customerId) =>
  `${root}/${encodeURIComponent(String(customerId))}/userInvites`;

const buildCustomerUserAccessUrl = (root, customerId) =>
  `${root}/${encodeURIComponent(String(customerId))}/users/access`;

const buildUserCustomerAccessUrl = (usersRoot, userId, customerId) =>
  `${usersRoot}/${encodeURIComponent(String(userId))}/customerAccess/${encodeURIComponent(String(customerId))}`;

export const UsersServices = () => {
  const apiPaths = INFRAMARK_CONSTANTS.users.services;
  const customerRoot = apiPaths.customerUsersRoot;
  const userInvites = apiPaths.userInvites;
  return {
    getAllUsers: async (customerId, page = 0, size = 20, filters = {}) => {
      const normalizedPage = Math.max(0, Number(page) || 0);
      const pageSize = Math.max(1, Number(size) || 20);
      const params = new URLSearchParams();
      params.set("page", String(normalizedPage));
      params.set("pageSize", String(pageSize));
      const emailQuery = String(filters.email ?? "").trim();
      const firstNameQuery = String(filters.firstName ?? "").trim();
      const lastNameQuery = String(filters.lastName ?? "").trim();
      const phoneQuery = String(filters.phone ?? "").trim();
      const roleQuery = String(filters.role ?? "").trim();
      const accessStatusQuery = String(filters.accessStatus ?? "").trim();
      if (emailQuery) {
        params.set("email", emailQuery);
      }
      if (firstNameQuery) {
        params.set("firstName", firstNameQuery);
      }
      if (lastNameQuery) {
        params.set("lastName", lastNameQuery);
      }
      if (phoneQuery) {
        params.set("phoneNumber", phoneQuery);
      }
      if (roleQuery) {
        params.set("role", roleQuery);
      }
      if (accessStatusQuery) {
        params.set("accessStatus", accessStatusQuery);
      }
      const url = `${buildCustomerUsersUrl(customerRoot, customerId)}?${params}`;
      try {
        const response = await getRequest(url);
        const statusNum = Number(response?.status);
        const failed =
          (Number.isFinite(statusNum) && statusNum >= 400) ||
          (response?.error != null && response?.error !== "");
        if (failed) {
          return {
            status: response?.status ?? 500,
            error: response?.error ?? "Unable to load users.",
          };
        }
        return response;
      } catch (error) {
        return {
          status: 500,
          error: error?.message ?? "Unable to load users.",
        };
      }
    },
    resendCustomerUserInvite: async (inviteId, body) => {
      const url = `${userInvites}/${encodeURIComponent(String(inviteId))}/resend`;
      return postRequest(url, body);
    },
    postCustomerUserInvite: async (customerId, body) => {
      const url = buildCustomerUserInvitesUrl(customerRoot, customerId);
      return postRequest(url, body);
    },
    patchCustomerUserAccess: async (customerId, body) => {
      const url = buildCustomerUserAccessUrl(customerRoot, customerId);
      return patchRequest(url, body);
    },
    patchUserCustomerAccess: async (userId, customerId, body) => {
      const url = buildUserCustomerAccessUrl(
        apiPaths.platformUsers,
        userId,
        customerId
      );
      return patchRequest(url, body);
    },
    checkInviteEmailExists: async (email, customerId) => {
      const trimmed = String(email ?? "").trim();
      if (!trimmed) {
        return { status: 200, data: { exists: false } };
      }
      const params = { email: trimmed };
      const normalizedCustomerId = String(customerId ?? "").trim();
      if (normalizedCustomerId) {
        params.customerId = normalizedCustomerId;
      }
      const response = await getRequest(apiPaths.usersExists, params);
      if (response?.error) {
        return response;
      }
      const existsResponseBody = response?.data;
      const existsFlag =
        existsResponseBody?.exists ?? existsResponseBody?.data?.exists;
      return {
        status: response?.status,
        data: { exists: existsFlag === true },
      };
    },
  };
};
